import { DropDown, DropDownMultiSelect, OdinController, OdinDataRetrieval } from '@myosh/odin-components';
import { t } from 'i18next';
import { useMemo, useState } from 'react';
import { Control, FieldErrors, UseFormGetValues, UseFormSetValue, UseFormWatch } from 'react-hook-form';
import toast from 'react-hot-toast';
import { useGetAiFieldTypesQuery } from '../../../redux/config/field-types-api';
import { useLazyGetFiltersQuery } from '../../../redux/config/filters-api';
import { RollingFR } from '../../../redux/config/widgetApi';
import { DefaultValueProps } from '../widget-settings-dialog';
import { LabelFormatType } from '../widget-settings-validation';

interface AiSettingTabProps {
  control: Control<DefaultValueProps>;
  aiConfiguration: RollingFR;
  watch: UseFormWatch<DefaultValueProps>;
  getValues: UseFormGetValues<DefaultValueProps>;
  setValue: UseFormSetValue<DefaultValueProps>;
  errors: FieldErrors<DefaultValueProps>;
}

const dropdownStyles = 'inline-block overflow-hidden py-0.5 pl-1.5';
const dropdownLabelStyles = 'font-bold text-xs';
const warningLabelStyles = 'box-border inline-block whitespace-normal align-top text-sm font-light text-error';

const AiSettingTab = ({ control, aiConfiguration, watch, getValues, setValue, errors }: AiSettingTabProps) => {
  const injuryFrequencyGroupingCriteria = watch(
    'injuryFrequencyGroupingCriteria',
    getValues('injuryFrequencyGroupingCriteria')
  );
  const injuryFrequencyFields = watch('injuryFrequencyFields', getValues('injuryFrequencyFields'));
  const injuryFrequencyInjuryClassificationFieldName = watch(
    'injuryFrequencyInjuryClassificationFieldName',
    getValues('injuryFrequencyInjuryClassificationFieldName')
  );

  const hoursWorkedGroupingCriteria = watch('hoursWorkedGroupingCriteria', getValues('hoursWorkedGroupingCriteria'));
  const hoursWorkedFields = watch('hoursWorkedFields', getValues('hoursWorkedFields'));

  const [injuryFrequencyMissingGroupCriteria, setInjuryFrequencyMissingGroupCriteria] = useState<string[]>([]);
  const [hoursWorkedMissingGroupCriteria, setHoursWorkedMissingGroupCriteria] = useState<string[]>([]);

  const { data: injuryFrequencyOptions } = useGetAiFieldTypesQuery(
    {
      module: String(aiConfiguration.module),
      form: aiConfiguration.form,
    },
    {
      skip: !String(aiConfiguration.module) || !aiConfiguration.form,
    }
  );

  const { data: hoursWorkedOptions } = useGetAiFieldTypesQuery(
    {
      module: String(aiConfiguration.hours.module),
      form: aiConfiguration.hours.form,
    },
    {
      skip: !String(aiConfiguration.hours.module) || !aiConfiguration.hours.form,
    }
  );

  const [triggerGetFilters] = useLazyGetFiltersQuery();

  const injuryFrequencyInjuryClassificationValuesDataRetrieval = useMemo<OdinDataRetrieval>(
    () => ({
      getData: async (subscriber, options) => {
        if (options?.page === 1) {
          try {
            const filterOptions = await triggerGetFilters({
              field: String(injuryFrequencyInjuryClassificationFieldName?.value),
              form: aiConfiguration.form,
            }).unwrap();

            subscriber.sendData({
              data: filterOptions.values.map((item) => ({
                field: item,
              })),
            });
          } catch (_error) {
            toast.error('Failed to load injury classification values.');
            subscriber.sendData();
          }
        } else {
          subscriber.sendData();
        }
      },
    }),
    [aiConfiguration.form, injuryFrequencyInjuryClassificationFieldName?.value, triggerGetFilters]
  );

  const handleDropdownChange = async (
    value: LabelFormatType[],
    type: string,
    controllerOnChange: (...event: unknown[]) => void
  ) => {
    controllerOnChange(value);

    switch (type) {
      case 'injuryFrequencyGroupingCriteria': {
        const updatedFields = [...(injuryFrequencyFields ?? [])];
        const groupValues = extractValues(value);
        const fieldValues = extractValues(updatedFields);
        const missingCriteria = groupValues.filter((val) => !fieldValues.includes(val));
        setInjuryFrequencyMissingGroupCriteria(missingCriteria);
        break;
      }

      case 'injuryFrequencyFields': {
        validateCriteria(
          injuryFrequencyGroupingCriteria,
          value,
          setInjuryFrequencyMissingGroupCriteria,
          'Fields must include all selected Grouping Criteria.'
        );
        break;
      }

      case 'hoursWorkedGroupingCriteria': {
        const updatedFields = [...(hoursWorkedFields ?? [])];
        const groupValues = extractValues(value);
        const fieldValues = extractValues(updatedFields);
        const missingCriteria = groupValues.filter((val) => !fieldValues.includes(val));
        setHoursWorkedMissingGroupCriteria(missingCriteria);
        break;
      }

      case 'hoursWorkedFields': {
        validateCriteria(
          hoursWorkedGroupingCriteria,
          value,
          setHoursWorkedMissingGroupCriteria,
          'Fields must include all selected Grouping Criteria.'
        );
        break;
      }

      case 'injuryFrequencyInjuryClassificationFieldName':
        setValue('injuryFrequencyInjuryClassificationValues', []);
        break;

      default:
        break;
    }
  };

  return (
    <div className="flex flex-col gap-3.5">
      <span className="whitespace-pre-wrap text-sm font-normal italic">
        {String(
          t(
            'aiSettingsMessage',
            'The usage of this property is to enable the user to select form, module and respective fields of the records which will be used for total recordable injury frequency analysis.'
          )
        )}
      </span>

      <span className={warningLabelStyles}>
        {String(
          t(
            'aiSettingsWarning',
            'If the grouping criteria are included, the values should also be included in the fields property.'
          )
        )}
      </span>

      <div className="flex flex-col gap-2">
        <span className="block pb-1 text-sm font-bold">{String(t('injuryFrequency', 'Injury Frequency'))}</span>
        <OdinController
          name="injuryFrequencyFields"
          control={control}
          rules={{ required: true }}
          render={({ field: { value, onChange } }) => (
            <>
              <DropDownMultiSelect
                useChips={false}
                required={1}
                label={'Fields'}
                data={injuryFrequencyOptions ?? []}
                value={value}
                allowClear={true}
                placeholder={`Select Fields`}
                textField="field"
                valueField="field"
                onChange={(value) => {
                  handleDropdownChange(value as LabelFormatType[], 'injuryFrequencyFields', onChange);
                }}
                labelStyles={dropdownLabelStyles}
                className={dropdownStyles}
              />
              {errors.injuryFrequencyFields && (
                <p className={warningLabelStyles}>{errors.injuryFrequencyFields.message}</p>
              )}
            </>
          )}
        />

        <OdinController
          name="injuryFrequencyInjuryClassificationFieldName"
          control={control}
          rules={{ required: true }}
          render={({ field: { onChange, value } }) => (
            <>
              <DropDown
                required={1}
                label={'Injury Classification Field Name'}
                data={injuryFrequencyOptions ?? []}
                value={value}
                allowClear={true}
                placeholder={`Select Injury Classification Field Name`}
                textField="field"
                valueField="field"
                onChange={(value) => {
                  handleDropdownChange(
                    value as LabelFormatType[],
                    'injuryFrequencyInjuryClassificationFieldName',
                    onChange
                  );
                }}
                labelStyles={dropdownLabelStyles}
                className={dropdownStyles}
              />
              {errors.injuryFrequencyInjuryClassificationFieldName && (
                <p className={warningLabelStyles}>{errors.injuryFrequencyInjuryClassificationFieldName.message}</p>
              )}
            </>
          )}
        />

        <OdinController
          name="injuryFrequencyInjuryClassificationValues"
          control={control}
          rules={{ required: true }}
          key={injuryFrequencyInjuryClassificationFieldName?.value}
          render={({ field: { onChange, value } }) => (
            <>
              <DropDownMultiSelect
                required={1}
                useChips={false}
                label={'Injury Classification Values'}
                data={injuryFrequencyInjuryClassificationValuesDataRetrieval}
                value={value}
                allowClear={true}
                placeholder={`Select Injury Classification Values`}
                textField="field"
                valueField="field"
                onChange={onChange}
                labelStyles={dropdownLabelStyles}
                className={dropdownStyles}
              />
              {errors.injuryFrequencyInjuryClassificationValues && (
                <p className={warningLabelStyles}>{errors.injuryFrequencyInjuryClassificationValues.message}</p>
              )}
            </>
          )}
        />

        <OdinController
          name="injuryFrequencyGroupingCriteria"
          control={control}
          render={({ field: { value, onChange } }) => (
            <DropDownMultiSelect
              useChips={false}
              label={'Grouping Criteria'}
              data={injuryFrequencyOptions ?? []}
              value={value}
              allowClear={true}
              placeholder={`Select Grouping Criteria`}
              textField="field"
              valueField="field"
              onChange={(value) => {
                handleDropdownChange(value as LabelFormatType[], 'injuryFrequencyGroupingCriteria', onChange);
              }}
              labelStyles={dropdownLabelStyles}
              className={dropdownStyles}
            />
          )}
        />

        {injuryFrequencyMissingGroupCriteria.length > 0 && (
          <span className={warningLabelStyles}>
            {`Missing grouping criteria in fields: ${injuryFrequencyMissingGroupCriteria.join(', ')}`}
          </span>
        )}

        <span className="block pb-1 text-sm font-bold">{String(t('hoursWorked', 'Hours Worked'))}</span>

        <OdinController
          name="hoursWorkedFields"
          control={control}
          rules={{ required: true }}
          render={({ field: { value, onChange } }) => (
            <>
              <DropDownMultiSelect
                useChips={false}
                required={1}
                label={'Fields'}
                data={hoursWorkedOptions ?? []}
                value={value}
                allowClear={true}
                placeholder={`Select Fields`}
                textField="field"
                valueField="field"
                onChange={(value) => {
                  handleDropdownChange(value as LabelFormatType[], 'hoursWorkedFields', onChange);
                }}
                labelStyles={dropdownLabelStyles}
                className={dropdownStyles}
              />
              {errors.hoursWorkedFields && <p className={warningLabelStyles}>{errors.hoursWorkedFields.message}</p>}
            </>
          )}
        />

        <OdinController
          name="hoursWorkedFieldName"
          control={control}
          rules={{ required: true }}
          render={({ field: { onChange, value } }) => (
            <>
              <DropDown
                required={1}
                label={'Hours Worked Field Name'}
                data={hoursWorkedOptions ?? []}
                value={value}
                allowClear={true}
                placeholder={`Select Field Name`}
                textField="field"
                valueField="field"
                onChange={onChange}
                labelStyles={dropdownLabelStyles}
                className={dropdownStyles}
              />
              {errors.hoursWorkedFieldName && (
                <p className={warningLabelStyles}>{errors.hoursWorkedFieldName.message}</p>
              )}
            </>
          )}
        />

        <OdinController
          name="hoursWorkedInjuryClassificationFieldName"
          control={control}
          render={({ field: { onChange, value } }) => (
            <>
              <DropDown
                label={'Injury Classification Field Name'}
                data={hoursWorkedOptions ?? []}
                value={value}
                allowClear={true}
                placeholder={`Select Injury Classification Field Name`}
                textField="field"
                valueField="field"
                onChange={onChange}
                labelStyles={dropdownLabelStyles}
                className={dropdownStyles}
              />
            </>
          )}
        />
        <OdinController
          name="hoursWorkedGroupingCriteria"
          control={control}
          render={({ field: { value, onChange } }) => (
            <DropDownMultiSelect
              useChips={false}
              label={'Grouping Criteria'}
              data={hoursWorkedOptions ?? []}
              value={value}
              allowClear={true}
              placeholder={`Select Grouping Criteria`}
              textField="field"
              valueField="field"
              onChange={(value) => {
                handleDropdownChange(value as LabelFormatType[], 'hoursWorkedGroupingCriteria', onChange);
              }}
              labelStyles={dropdownLabelStyles}
              className={dropdownStyles}
            />
          )}
        />
        {hoursWorkedMissingGroupCriteria.length > 0 && (
          <span className={warningLabelStyles}>
            {`Missing grouping criteria in fields: ${hoursWorkedMissingGroupCriteria.join(', ')}`}
          </span>
        )}
      </div>
    </div>
  );
};

const extractValues = (items: LabelFormatType[] = []) => items.map((item) => item.value);

const validateCriteria = (
  groupCriteria: LabelFormatType[] = [],
  selectedFields: LabelFormatType[],
  setMissing: (missing: string[]) => void,
  errorMsg: string
) => {
  const groupValues = extractValues(groupCriteria);
  const fieldValues = extractValues(selectedFields);

  const missingCriteria = groupValues.filter((val) => !fieldValues.includes(val));
  setMissing(missingCriteria);

  if (missingCriteria.length > 0) {
    toast.error(errorMsg);
  }
};

export default AiSettingTab;
